// src/admin/AdminConversionHistory.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TablePagination,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Button,
  Tooltip,
  Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../utils/api";
import { API } from "../config/apiConfig";
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

const HISTORY_ENDPOINT =
  API.adminConversionsHistory ||
  (API.adminConversions ? `${API.adminConversions}/history` : "/api/admin/conversions/history");

function formatDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
function firstOfThisMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}
function lastOfThisMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
}
function fmtNum(val, decimals = 2) {
  const n = Number(val ?? 0);
  return Number.isFinite(n) ? n.toFixed(decimals) : "0.00";
}
function fmtCurrency(val) {
  const n = Number(val ?? 0);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "$0.00";
}

export default function AdminConversionHistory() {
  // filters
  const [start, setStart] = useState(firstOfThisMonth());
  const [end, setEnd] = useState(lastOfThisMonth());
  const [searchTerm, setSearchTerm] = useState("");

  // table state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // status
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const headerText = useMemo(
    () => `${formatDate(start)} → ${formatDate(end)}`,
    [start, end]
  );

  // fetch
  const fetchHistory = async ({ resetPage = false } = {}) => {
    setLoading(true);
    try {
      const params = {
        periodStart: formatDate(start),
        periodEnd: formatDate(end),
        searchTerm: searchTerm || undefined,
        page: resetPage ? 1 : page + 1, // backend pages are often 1-based
        pageSize: rowsPerPage,
        sortBy: "CreatedAt",
        sortDir: "DESC",
      };

      const { data } = await api.get(HISTORY_ENDPOINT, { params });

      // Flexible response handling:
      // Prefer { items, total }, else { history, total }, else array
      const items = data?.items ?? data?.history ?? (Array.isArray(data) ? data : []);
      const count = data?.total ?? data?.count ?? items.length;

      setRows(items || []);
      setTotal(Number(count) || 0);
      setSuccessMsg("History loaded");
      if (resetPage) setPage(0);
    } catch (err) {
      console.error("❌ Failed to fetch conversion history:", err);
      setErrorMsg(err?.response?.data?.error || "Failed to fetch conversion history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handlers
  const handleThisMonth = async () => {
    setStart(firstOfThisMonth());
    setEnd(lastOfThisMonth());
    await fetchHistory({ resetPage: true });
  };

  const handleStartChange = (e) => {
    const d = new Date(e.target.value);
    if (!isNaN(d)) {
      setStart(d);
      if (end < d) setEnd(d);
    }
  };
  const handleEndChange = (e) => {
    const d = new Date(e.target.value);
    if (!isNaN(d) && d >= start) {
      setEnd(d);
    }
  };

  const handleChangePage = async (_evt, newPage) => {
    setPage(newPage);
    await fetchHistory();
  };

  const handleChangeRowsPerPage = async (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    await fetchHistory({ resetPage: true });
  };

  const handleSearchKeyUp = async (e) => {
    if (e.key === "Enter") {
      await fetchHistory({ resetPage: true });
    }
  };

  const handleRefresh = async () => {
    await fetchHistory({ resetPage: true });
  };

  const exportCsv = () => {
    // Export currently loaded rows (respecting filters & current page)
    const headers = [
      "ConversionID",
      "UserID",
      "UserName",
      "Email",
      "AppId",
      "AppName",
      "PeriodStart",
      "PeriodEnd",
      "TokensConverted",
      "ConversionRate",
      "CashEquivalent",
      "Source",
      "CreatedAt",
    ];

    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        [
          r.ConversionID ?? r.Id ?? "",
          r.UserID ?? "",
          csvSafe(r.UserName),
          csvSafe(r.Email),
          r.AppId ?? "",
          csvSafe(r.AppName),
          r.PeriodStart ? formatDate(r.PeriodStart) : "",
          r.PeriodEnd ? formatDate(r.PeriodEnd) : "",
          r.TokenBalanceAtConversion ?? r.TokensConverted ?? r.TotalTokens ?? "",
          r.ConversionRate ?? "",
          r.CashEquivalent ?? r.AmountUSD ?? "",
          csvSafe(r.Source),
          r.CreatedAt ? new Date(r.CreatedAt).toISOString() : "",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversion-history_${formatDate(start)}_${formatDate(end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminPageWrapper 
      title="Conversion History" 
      subtitle="View detailed token conversion history and transactions"
    >

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
        <TextField
          label="Start"
          type="date"
          value={formatDate(start)}
          onChange={handleStartChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={(theme) => ({
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(102, 126, 234, 0.5)',
              },
              '&.Mui-focused': {
                borderColor: '#4FACFE',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              },
            },
          })}
        />
        <TextField
          label="End"
          type="date"
          value={formatDate(end)}
          onChange={handleEndChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={(theme) => ({
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(102, 126, 234, 0.5)',
              },
              '&.Mui-focused': {
                borderColor: '#4FACFE',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              },
            },
          })}
        />
        <TextField
          label="Search (user, email, app)"
          placeholder="Type and press Enter…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyUp={handleSearchKeyUp}
          size="small"
          sx={(theme) => ({
            flex: 1,
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(102, 126, 234, 0.5)',
              },
              '&.Mui-focused': {
                borderColor: '#4FACFE',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              },
            },
          })}
        />

        <Tooltip title="Refresh">
          <span>
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
              sx={(theme) => ({
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(255,255,255,0.9)',
                },
              })}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="outlined"
          startIcon={<CalendarMonthIcon />}
          onClick={handleThisMonth}
          size="small"
          sx={(theme) => ({
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.2)'
              : 'rgba(0,0,0,0.2)',
            color: 'text.primary',
            '&:hover': {
              borderColor: '#4FACFE',
              background: theme.palette.mode === 'dark'
                ? 'rgba(102, 126, 234, 0.1)'
                : 'rgba(102, 126, 234, 0.05)',
            },
          })}
        >
          This Month
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportCsv}
          disabled={!rows?.length}
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Export CSV
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
        Period: {headerText}
      </Typography>

      {/* Table */}
      <ProfessionalTable
        columns={[
          {
            header: 'Date',
            accessor: 'CreatedAt',
            render: (value) => (
              <Typography variant="body2">
                {value ? new Date(value).toLocaleString() : "—"}
              </Typography>
            )
          },
          {
            header: 'Period',
            accessor: (row) => {
              const periodStart = row.PeriodStart ? formatDate(row.PeriodStart) : "";
              const periodEnd = row.PeriodEnd ? formatDate(row.PeriodEnd) : "";
              return periodStart && periodEnd ? `${periodStart} → ${periodEnd}` : periodStart || periodEnd || "—";
            },
            render: (value) => (
              <Typography variant="body2" color="text.secondary">
                {value}
              </Typography>
            )
          },
          {
            header: 'App',
            accessor: 'AppName',
            render: (value, row) => (
              <Typography variant="body2">
                {value || row.AppId || "—"}
              </Typography>
            )
          },
          {
            header: 'User',
            accessor: (row) => {
              if (row.UserName || row.Email) {
                return `${row.UserName ?? ""}${row.Email ? ` (${row.Email})` : ""}`;
              }
              return row.UserID || "—";
            },
            render: (value) => (
              <Typography variant="body2">
                {value}
              </Typography>
            )
          },
          {
            header: 'Tokens',
            accessor: (row) => row.TokenBalanceAtConversion ?? row.TokensConverted ?? row.TotalTokens ?? 0,
            render: (value) => (
              <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                {fmtNum(value, 0)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'Rate',
            accessor: 'ConversionRate',
            render: (value) => (
              <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                ${fmtNum(value ?? 0, 6)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'USD',
            accessor: (row) => row.CashEquivalent ?? row.AmountUSD ?? 0,
            render: (value) => (
              <Typography variant="body2" sx={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}>
                {fmtCurrency(value)}
              </Typography>
            ),
            style: { textAlign: 'right' }
          },
          {
            header: 'Source',
            accessor: 'Source',
            render: (value) => (
              <Typography variant="body2" color="text.secondary">
                {value || "—"}
              </Typography>
            )
          }
        ]}
        data={rows.map((r, index) => ({ ...r, id: r.ConversionID ?? r.Id ?? index }))}
        loading={loading}
        emptyMessage="No records found"
      />

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          color: 'text.primary',
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: 'text.primary',
          },
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!errorMsg || !!successMsg}
        autoHideDuration={3500}
        onClose={() => {
          setErrorMsg("");
          setSuccessMsg("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={errorMsg ? "error" : "success"}
          onClose={() => {
            setErrorMsg("");
            setSuccessMsg("");
          }}
          sx={{ width: "100%" }}
        >
          {errorMsg || successMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

function csvSafe(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
